import { useCallback, useEffect, useState } from "react";
import { Activity, Download, Filter, RefreshCw } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Tabs, TabPanel } from "@/components/ui/Tabs";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/useToast";
import {
  exportAuditLogs,
  listAuditActions,
  listAuditLogs,
  type AuditLogEntry,
  type AuditLogFilters,
} from "@/api/auditLogs";
import { AIGovernanceTab } from "./AIGovernanceTab";

/**
 * Top-level Audit Log page (HR_ADMIN / SUPER_ADMIN).
 *
 * Same cursor-paginated view as settings, with a date range, actor-name
 * column, expandable old→new diff, and CSV export.
 */
type AuditTabId = "activity" | "ai";

export function AuditLogPage() {
  const { addToast } = useToast();
  const [tab, setTab] = useState<AuditTabId>("activity");

  const [actionInput, setActionInput] = useState("");
  const [entityTypeInput, setEntityTypeInput] = useState("");
  const [entityIdInput, setEntityIdInput] = useState("");
  const [userIdInput, setUserIdInput] = useState("");
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");

  const [applied, setApplied] = useState<AuditLogFilters>({});
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const fetchPage = useCallback(
    async (reset: boolean) => {
      setLoading(true);
      try {
        const filters: AuditLogFilters = { ...applied, limit: 50 };
        if (!reset && cursor) filters.before = cursor;
        const page = await listAuditLogs(filters);
        setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
        setCursor(page.next_before);
        setHasMore(page.has_more);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to load audit log";
        addToast("error", msg);
      } finally {
        setLoading(false);
      }
    },
    [applied, cursor, addToast],
  );

  useEffect(() => {
    void fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  useEffect(() => {
    listAuditActions()
      .then(setActions)
      .catch(() => setActions([]));
  }, []);

  const onApply = () => {
    const next: AuditLogFilters = {};
    if (actionInput.trim()) next.action = actionInput.trim();
    if (entityTypeInput.trim()) next.entity_type = entityTypeInput.trim();
    if (entityIdInput.trim()) next.entity_id = entityIdInput.trim();
    if (userIdInput.trim()) next.user_id = userIdInput.trim();
    if (startDateInput) next.start_date = startDateInput;
    if (endDateInput) next.end_date = endDateInput;
    setApplied(next);
  };

  const onClear = () => {
    setActionInput("");
    setEntityTypeInput("");
    setEntityIdInput("");
    setUserIdInput("");
    setStartDateInput("");
    setEndDateInput("");
    setApplied({});
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await exportAuditLogs(applied);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addToast("success", "Audit log exported");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Export failed";
      addToast("error", msg);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Log"
        subtitle="Every administrative action and AI decision, captured for compliance."
        actions={
          tab === "activity" ? (
            <Button onClick={onExport} disabled={exporting}>
              <Download className="mr-2 h-4 w-4" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          ) : null
        }
      />

      <Tabs
        tabs={[
          { id: "activity", label: "Activity" },
          { id: "ai", label: "AI Governance" },
        ]}
        activeTab={tab}
        onChange={(id) => setTab(id as AuditTabId)}
        aria-label="Audit log sections"
      />

      <TabPanel id="ai" activeTab={tab}>
        <AIGovernanceTab />
      </TabPanel>

      <TabPanel id="activity" activeTab={tab}>
        <div className="space-y-6">

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Activity
          </CardTitle>
        </CardHeader>

        <div className="space-y-4 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <div>
              <label className="mb-1 block text-xs font-medium">Action</label>
              <Input
                list="audit-actions"
                value={actionInput}
                placeholder="e.g. login.failed"
                onChange={(e) => setActionInput(e.target.value)}
              />
              <datalist id="audit-actions">
                {actions.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Entity type</label>
              <Input
                value={entityTypeInput}
                placeholder="employee"
                onChange={(e) => setEntityTypeInput(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Entity ID</label>
              <Input
                value={entityIdInput}
                placeholder="UUID"
                onChange={(e) => setEntityIdInput(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Actor user ID</label>
              <Input
                value={userIdInput}
                placeholder="UUID"
                onChange={(e) => setUserIdInput(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Start date</label>
              <Input
                type="date"
                value={startDateInput}
                onChange={(e) => setStartDateInput(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">End date</label>
              <Input
                type="date"
                value={endDateInput}
                onChange={(e) => setEndDateInput(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={onApply} disabled={loading}>
              <Filter className="mr-2 h-4 w-4" /> Apply
            </Button>
            <Button variant="ghost" onClick={onClear} disabled={loading}>
              Clear
            </Button>
            <Button
              variant="ghost"
              onClick={() => void fetchPage(true)}
              disabled={loading}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Entity</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr>
                    <td
                      className="px-3 py-6 text-center text-muted-foreground"
                      colSpan={6}
                    >
                      No audit entries match.
                    </td>
                  </tr>
                )}
                {items.map((row) => {
                  const isOpen = !!expanded[row.id];
                  return (
                    <tr key={row.id} className="border-t align-top">
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {row.user_name ? (
                          <>
                            <div>{row.user_name}</div>
                            {row.user_email && (
                              <div className="text-muted-foreground">
                                {row.user_email}
                              </div>
                            )}
                          </>
                        ) : row.user_id ? (
                          <span className="font-mono">{row.user_id}</span>
                        ) : (
                          <span className="text-muted-foreground">system</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge>{row.action}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs">{row.entity_type}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {row.entity_id ?? "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.ip_address ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setExpanded((prev) => ({
                              ...prev,
                              [row.id]: !prev[row.id],
                            }))
                          }
                        >
                          {isOpen ? "Hide" : "Show"}
                        </Button>
                        {isOpen && (
                          <div className="mt-2 grid max-w-2xl gap-2 md:grid-cols-2">
                            <div>
                              <div className="mb-1 text-xs font-medium text-muted-foreground">
                                Before
                              </div>
                              <pre className="overflow-auto rounded bg-muted/40 p-2 text-xs">
{JSON.stringify(row.old_values ?? null, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <div className="mb-1 text-xs font-medium text-muted-foreground">
                                After
                              </div>
                              <pre className="overflow-auto rounded bg-muted/40 p-2 text-xs">
{JSON.stringify(row.new_values ?? null, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              Showing {items.length} entr{items.length === 1 ? "y" : "ies"}.
            </div>
            <Button
              variant="ghost"
              onClick={() => void fetchPage(false)}
              disabled={!hasMore || loading}
            >
              {loading ? "Loading…" : hasMore ? "Load more" : "End of log"}
            </Button>
          </div>
        </div>
      </Card>
        </div>
      </TabPanel>
    </div>
  );
}
